// Recording orchestration (F-REC-1 + F-REC-2, §10). Ties three concerns:
//   1. the trust indicator (broadcast rec:on while capturing),
//   2. the composite capture (CompositeRecorder),
//   3. an OPTIONAL encrypted upload (§10.4(c)) — the recording is encrypted
//      client-side (keyed off the room secret, like a shared file) and only
//      ciphertext reaches S3; the server can never decrypt it.
//
// Per §10.4 the primary output is a local save; upload is opt-in. So `stop()`
// hands back the plain webm and `uploadEncrypted()` is a separate step.
//
// Layout stays in the UI: the caller supplies `drawFrame`, which paints the
// current tiles onto the recorder canvas each frame.

import { CallEngine } from '../room';
import { CompositeRecorder, RecordingResult, recordingSupported } from './recorder';

export interface RecordingUpload {
  /** Presign + PUT the ciphertext; returns the stored object key. The UI owns
   * this because it holds the room token for presign. */
  putCiphertext: (fileId: string, ciphertext: ArrayBuffer) => Promise<string>;
}

export interface UploadedRecording {
  fileId: string;
  objectKey: string;
  nonceB64: string;
  sizeBytes: number;
}

export type DrawFrame = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
) => void;

export class RecordingController {
  private recorder: CompositeRecorder | null = null;

  constructor(private room: CallEngine) {}

  static supported(): boolean {
    return recordingSupported();
  }

  get active(): boolean {
    return this.recorder?.active ?? false;
  }

  /** Begin recording: raise the indicator FIRST (so no frame is captured before
   * attendees are told, §10.5), then start capture. */
  async start(byName: string, drawFrame: DrawFrame): Promise<void> {
    if (this.recorder) throw new Error('already recording');
    if (!recordingSupported()) throw new Error('recording unsupported in this browser');

    this.room.startRecordingIndicator(byName);
    try {
      this.recorder = new CompositeRecorder({
        draw: drawFrame,
        audioTracks: () => this.room.recordingAudioTracks(),
      });
      await this.recorder.start();
    } catch (err) {
      // Capture failed to start — retract the indicator so we never claim to be
      // recording when we aren't.
      this.room.stopRecordingIndicator();
      this.recorder = null;
      throw err;
    }
  }

  /** Stop capture and drop the indicator; returns the finalized plain webm. */
  async stop(): Promise<RecordingResult> {
    const recorder = this.recorder;
    if (!recorder) throw new Error('not recording');
    try {
      return await recorder.stop();
    } finally {
      this.recorder = null;
      this.room.stopRecordingIndicator();
    }
  }

  /** F-REC-2: encrypt the finished recording client-side and upload ciphertext.
   * Optional and post-stop. The decryption key stays with the recorder (§10.4). */
  async uploadEncrypted(result: RecordingResult, upload: RecordingUpload): Promise<UploadedRecording> {
    const bytes = await result.blob.arrayBuffer();
    const { fileId, ciphertext, nonceB64 } = await this.room.encryptFileForSharing(bytes);
    const objectKey = await upload.putCiphertext(fileId, ciphertext);
    return { fileId, objectKey, nonceB64, sizeBytes: ciphertext.byteLength };
  }
}
